import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import FormField from './FormField'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  lineNumbers: {
    // 高度由輸入框撐開的容器決定（stretch），內容改用 absolute 脫離排版，
    // 避免行號欄的高度反過來撐住輸入框，造成一次刪除多行後高度不會縮回
    position: 'relative',
    alignSelf: 'stretch',
    overflow: 'hidden',
    marginRight: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    textAlign: 'right',
    userSelect: 'none'
  },
  lineNumbersSizer: {
    height: 0,
    overflow: 'hidden',
    visibility: 'hidden'
  },
  lineNumbersInner: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0
  }
}))

const LineNumberTextArea = function <T, U>(props: Parameters<typeof FormField<T, U>>[0] & { alwaysShowLineNumber?: boolean }) {
  const { context, name, alwaysShowLineNumber = false } = props
  const minRows = ('minRows' in props && Number(props.minRows)) || 1
  const { value } = useContext(context)
  const classes = useStyles()
  const [scrollTop, setScrollTop] = useState<number>(0)
  const [metrics, setMetrics] = useState({ lineHeight: 0, offsetTop: 0, height: 0 })
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const gutterRef = useRef<HTMLDivElement | null>(null)

  const lineCount = useMemo(() => {
    const text = String(value[name] ?? '')
    const textLineCount = text ? text.split('\n').length : 1

    // 開啟時即使內容未滿，也顯示輸入框 minRows 範圍內的所有行號
    return alwaysShowLineNumber ? Math.max(textLineCount, minRows) : textLineCount
  }, [value, name, alwaysShowLineNumber, minRows])

  // 行號的行高與位置直接量測輸入框，避免與 MUI 的內距/行高實作細節脫鉤
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    const gutter = gutterRef.current
    if (!textarea || !gutter) return
    const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight)
    if (Number.isNaN(lineHeight)) return
    const offsetTop = textarea.getBoundingClientRect().top - gutter.getBoundingClientRect().top
    const height = textarea.clientHeight
    setMetrics((last) => (
      last.lineHeight === lineHeight && last.offsetTop === offsetTop && last.height === height
        ? last
        : { lineHeight, offsetTop, height }
    ))
    // 內容一次縮短時瀏覽器會把 scrollTop 夾回去，但不一定觸發 scroll 事件，這裡手動同步
    setScrollTop(textarea.scrollTop)
  }, [lineCount])

  const handleScroll = useCallback((event: React.UIEvent<HTMLTextAreaElement>) => {
    setScrollTop(event.currentTarget.scrollTop)
  }, [])

  const { alwaysShowLineNumber: _, ...passProps } = props

  const fieldProps = {
    ...passProps,
    multiline: true,
    inputRef: textareaRef,
    InputProps: {
      startAdornment: (
        <div className={classes.lineNumbers} ref={gutterRef}>
          {/* 內容是 absolute，寬度要靠這個不可見、高度為 0 的最大行號撐開 */}
          <div className={classes.lineNumbersSizer} aria-hidden>{lineCount}</div>
          <div className={classes.lineNumbersInner} style={{ lineHeight: `${metrics.lineHeight}px`, transform: `translateY(${metrics.offsetTop - scrollTop}px)` }}>
            {Array.from({ length: lineCount }, (_, index) => (<div key={index}>{index + 1}</div>))}
          </div>
        </div>
      )
    },
    inputProps: { wrap: 'off', onScroll: handleScroll }
  }

  return (
    <FormField<T, Omit<U, 'alwaysShowLineNumber'>> {...fieldProps} />
  )
}

export default LineNumberTextArea
